import { useCallback, useEffect, useRef } from 'react';
import {
  isTopLayer,
  lockScroll,
  nextLayerId,
  pushLayer,
  reclaimLayerEntry,
  releaseLayerEntry,
  removeLayer,
  scheduleLayerBack,
  unlockScroll,
  type LayerCloseReason,
} from './layerStack';

export interface LayerOptions {
  /** Блокировать скролл страницы (модалки — да, боковая панель видео — только на мобильном, где она во весь экран). */
  lockScroll?: boolean;
  /** Query-строка, которую слой держит в адресе, например `?video=12` (CAT-11). */
  urlParam?: string;
  /** Закрывать по Esc (по умолчанию да). */
  closeOnEscape?: boolean;
  /** false — слой не пишет историю сам (адрес ведёт роутер), участвует только в Esc и порядке слоёв. */
  history?: boolean;
}

/**
 * Интеграция слоя с историей и клавиатурой (NAV-10): при открытии добавляется
 * запись истории, «Назад» и Esc закрывают верхний слой. Deep-link (адрес уже
 * совпадает с urlParam) записи не добавляет и при закрытии чистит query через
 * replaceState — без лишней записи в истории.
 */
export function useLayer(
  open: boolean,
  onClose: (reason?: LayerCloseReason) => void,
  options: LayerOptions = {},
): { replaceUrl: (urlParam: string) => void; isTop: () => boolean } {
  const idRef = useRef<string>('');
  if (!idRef.current) idRef.current = nextLayerId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const activeRef = useRef(false);
  const {
    lockScroll: shouldLock = true,
    urlParam,
    closeOnEscape = true,
    history: ownHistory = true,
  } = options;

  useEffect(() => {
    if (!open) return;
    const id = idRef.current;
    const url = urlParam ? `${window.location.pathname}${urlParam}` : undefined;
    const currentUrl = window.location.pathname + window.location.search;
    const isDeepLink = url !== undefined && url === currentUrl;
    let pushed = false;
    let popped = false;
    const hrefAtOpen = window.location.href;

    if (!ownHistory) {
      // историей управляет роутер
    } else if (reclaimLayerEntry(id)) {
      // Немедленное ремонтирование: запись истории ещё наша.
      pushed = true;
    } else if (!isDeepLink) {
      window.history.pushState({ layer: id }, '', url);
      pushed = true;
    }
    pushLayer({
      id,
      closeOnEscape,
      historyless: !ownHistory,
      onClose: (reason) => onCloseRef.current(reason),
      markPopped: () => {
        popped = true;
      },
    });
    activeRef.current = true;
    if (shouldLock) lockScroll();

    return () => {
      removeLayer(id);
      activeRef.current = false;
      if (shouldLock) unlockScroll();
      const state = window.history.state as { layer?: string } | null;
      if (pushed && !popped && state?.layer === id) {
        // Адрес не менялся — снимаем свою запись (отложенно), глобальный слушатель поглотит popstate.
        // Адрес изменился (фильтры внутри шита) — запись остаётся, иначе выбор откатился бы вместе с ней.
        if (window.location.href === hrefAtOpen) scheduleLayerBack(id);
        else releaseLayerEntry();
      } else if (isDeepLink && urlParam && !popped) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    };
    // urlParam меняется через replaceUrl без перемонтирования.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const replaceUrl = useCallback((nextParam: string) => {
    if (!activeRef.current) return;
    window.history.replaceState(
      { layer: idRef.current },
      '',
      `${window.location.pathname}${nextParam}`,
    );
  }, []);

  const isTop = useCallback(() => isTopLayer(idRef.current), []);

  return { replaceUrl, isTop };
}
